import { memo, useState, useRef } from 'react';
import {
  Button,
  Flex,
  Text,
  Popover,
  PopoverTrigger,
  Portal,
  PopoverContent,
  Box,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useOutsideClick,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import ShareButtonWithPopup from 'components/Header/ShareButtonWithPopup';
import ShareIcon from 'components/Icons/ShareIcon';
// import LightFilledUserAvatar from 'components/Icons/FilledLightUserAvatar';
import LogoutModal from 'components/LogoutModal';
import { getUserData } from 'redux/UserSlice';
import HelpV1Icon from 'components/Icons/HelpV1Icon';
import FigmaHelpCircleIcon from 'components/Icons/FigmaHelpCircleIcon';
import FigmaHeadsetIcon from 'components/Icons/FigmaHeadsetIcon';
import UserIconV1 from 'components/UserIconV1';
import { neutral, radius, shadow, text as textToken } from 'styles/revampTokens';

import UserMenuPanel from './UserMenuPanel';

const UserActions = memo(({ globalHeader }: { globalHeader?: boolean }) => {
  const userData = useSelector(getUserData);
  const [logoutModal, setLogoutModal] = useState(false);
  const shareButtonReference = useRef<HTMLDivElement>(null);
  const [isUserPopoverOpen, setIsUserPopoverOpen] = useState(false);
  const userPopoverReference = useRef<HTMLDivElement | null>(null);

  useOutsideClick({
    ref: userPopoverReference,
    handler: () => {
      if (isUserPopoverOpen) {
        setIsUserPopoverOpen(false);
      }
    },
  });

  const handleMenuItemClick = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <>
      <Flex alignItems='center' gap={'12px'} height={'32px'} flexShrink={0} width='auto'>
        {!globalHeader && (
          <>
            <Menu>
              {({ isOpen }) => {
                return (
                  <>
                    <MenuButton
                      as={Button}
                      variant='unstyled'
                      p={0}
                      minW='0px'
                      width='30px'
                      height='30px'
                      aria-label='Help'
                    >
                      <div
                        style={{
                          width: '33px',
                          height: '33px',
                          padding: 8,
                          background: 'white',
                          borderRadius: 50,
                          outline: '1px #D1D6DD solid',
                          outlineOffset: '-1px',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: 8,
                          display: 'inline-flex',
                        }}
                      >
                        <HelpV1Icon />
                      </div>
                    </MenuButton>
                    {isOpen && (
                      // Plain icon + label rows, no colour-bubble icon wells — matches
                      // the rest of the revamp's neutral chrome (accent reserved for
                      // interactive state, not resting iconography). A "What's New"
                      // row existed here commented-out with no link ever wired up in
                      // any header variant; left out rather than guessing a URL.
                      // The hairline between rows is the Figma frame's own
                      // trick: the list itself is border-coloured and the white
                      // rows sit on it with a 1px gap, so no row needs its own
                      // divider (and none shows above the first / below the
                      // last). Scaled down from Figma's 16px padding / 20px
                      // icons to sit with the rest of the header chrome.
                      <MenuList
                        zIndex={9}
                        minW='184px'
                        p={0}
                        overflow='hidden'
                        display='flex'
                        flexDirection='column'
                        gap='1px'
                        bg={neutral.border}
                        borderRadius={radius.overlay}
                        boxShadow={shadow.overlay}
                        border='1px solid'
                        borderColor={neutral.border}
                      >
                        <MenuItem
                          py='9px'
                          px='12px'
                          bg={neutral.surface}
                          onClick={() => handleMenuItemClick('https://help.blox.so/')}
                          _hover={{ bg: neutral.surfaceHover }}
                          _focus={{ bg: neutral.surfaceHover }}
                        >
                          <Flex alignItems='center' gap='9px'>
                            <FigmaHelpCircleIcon
                              width={16}
                              height={16}
                              color={textToken.secondary}
                            />
                            <Text fontSize='13px' fontWeight='500' color={textToken.primary}>
                              Help Center
                            </Text>
                          </Flex>
                        </MenuItem>
                        <MenuItem
                          py='9px'
                          px='12px'
                          bg={neutral.surface}
                          onClick={() => handleMenuItemClick('https://www.blox.so/contact')}
                          _hover={{ bg: neutral.surfaceHover }}
                          _focus={{ bg: neutral.surfaceHover }}
                        >
                          <Flex alignItems='center' gap='9px'>
                            <FigmaHeadsetIcon width={16} height={16} color={textToken.secondary} />
                            <Text fontSize='13px' fontWeight='500' color={textToken.primary}>
                              Contact Support
                            </Text>
                          </Flex>
                        </MenuItem>
                      </MenuList>
                    )}
                  </>
                );
              }}
            </Menu>

            <Tooltip isDisabled label='Share model' hasArrow placement='bottom'>
              <Button
                variant='solid'
                bg='white'
                borderRadius={radius.control}
                border='1px solid #D1D6DD'
                height='30px'
                minW='36px'
                width='fit-content'
                p={0}
                _hover={{ bg: 'gray.50' }}
                aria-label='Share modal'
                onClick={() => {
                  // Trigger ShareButtonWithPopup's button click
                  const shareButton = shareButtonReference.current?.querySelector('button');
                  if (shareButton) {
                    shareButton.click();
                  }
                }}
              >
                <Flex alignItems='center' justifyContent='center' gap={2} px={3}>
                  <ShareIcon />
                  <Text fontSize='12px' fontWeight='500' color='#121826'>
                    Share model
                  </Text>
                </Flex>
              </Button>
            </Tooltip>

            <Box
              ref={shareButtonReference}
              position='absolute'
              visibility='hidden'
              pointerEvents='none'
            >
              <ShareButtonWithPopup />
            </Box>
          </>
        )}
        <Popover
          isLazy
          gutter={16}
          placement='bottom-end'
          isOpen={isUserPopoverOpen}
          onClose={() => setIsUserPopoverOpen(false)}
        >
          <Box ref={userPopoverReference}>
            <PopoverTrigger>
              <Button
                variant='solid'
                bg='white'
                borderRadius={radius.control}
                width='fit-content'
                height='30px'
                border='1px solid #D1D6DD'
                px={2}
                _hover={{ bg: 'gray.50' }}
                aria-label='Open user menu'
                onClick={() => setIsUserPopoverOpen((previous) => !previous)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <UserIconV1 />
                  <Text fontSize='12px' fontWeight='500' color='black'>
                    {`${userData?.firstname} ${userData?.lastname}`}
                  </Text>
                </div>
              </Button>
            </PopoverTrigger>
            <Portal>
              <PopoverContent
                position='absolute'
                right='0'
                p={0}
                w='fit-content'
                minWidth='280px'
                color='black'
                borderWidth='1px'
                borderRadius={radius.overlay}
                borderColor='gray.200'
                bg='white'
                boxShadow='0px 4px 12px rgba(0, 0, 0, 0.08)'
                mr='2'
              >
                <UserMenuPanel
                  onRequestClose={() => setIsUserPopoverOpen(false)}
                  onLogoutRequest={() => setLogoutModal(true)}
                />
              </PopoverContent>
            </Portal>
          </Box>
        </Popover>
      </Flex>

      {logoutModal && (
        <LogoutModal
          isOpen={logoutModal}
          onClose={() => setLogoutModal(false)}
          onOpenAuthModal={() => {}}
        />
      )}
    </>
  );
});

UserActions.displayName = 'UserActions';

export default UserActions;
